import { GridSortModel } from "@mui/x-data-grid";
import { Dayjs } from "dayjs";
import { DealerAccount } from "../../../../api/dealer/types";
import { OrdersResponse } from "../../../../api/orders/orderInterfaces";
import { AllOrdersFilters, OrderStatusCount } from "../types";

export enum AllOrdersActionsType {
  SET_ALL_ORDERS = "SET_ALL_ORDERS",
  SET_IS_LOADING = "SET_IS_LOADING",
  SET_IS_DOWNLOADING = "SET_IS_DOWNLOADING",
  SET_IS_TECHNICAL_ERROR = "SET_IS_TECHNICAL_ERROR",
  SET_IS_AUTHORIZATION_ERROR = "SET_IS_AUTHORIZATION_ERROR",
  SET_IS_AUTHENTICATION_ERROR = "SET_IS_AUTHENTICATION_ERROR",
  SET_IS_DOWNLOAD_FAILED = "SET_IS_DOWNLOAD_FAILED",
  SET_TAB_DOWNLOADING = "SET_TAB_DOWNLOADING",
  SET_TAB_DOWNLOAD_FAILED = "SET_TAB_DOWNLOAD_FAILED",
  SET_FILTER = "SET_FILTER",
  SET_LANGUAGE = "SET_LANGUAGE",
  SET_CURRENT_PAGE = "SET_CURRENT_PAGE",
  SET_PAGE_ROW_SIZE = "SET_PAGE_ROW_SIZE",
  SET_TOTAL_ORDERS = "SET_TOTAL_ORDERS",
  UPDATE_SELECTED_ACCOUNTS = "UPDATE_SELECTED_ACCOUNTS",
  SET_SORT_MODEL = "SET_SORT_MODEL",
  SET_DATE_RANGE_FILTER_ACTION = "SET_DATE_RANGE_FILTER_ACTION",
  SET_TAB_INDEX = "SET_TAB_INDEX",
  SET_STATUS_COUNT = "SET_STATUS_COUNT",
}

type SetAllOrdersAction = {
  type: AllOrdersActionsType.SET_ALL_ORDERS;
  payload: {
    allOrders: OrdersResponse[];
    totalOrders: number;
  };
};

type SetIsLoadingAction = {
  type: AllOrdersActionsType.SET_IS_LOADING;
  payload: boolean;
};

type SetIsDownloadingAction = {
  type: AllOrdersActionsType.SET_IS_DOWNLOADING;
  payload: boolean;
};

type SetIsTechnicalErrorAction = {
  type: AllOrdersActionsType.SET_IS_TECHNICAL_ERROR;
  payload: boolean;
};

type SetIsDownloadFailedErrorAction = {
  type: AllOrdersActionsType.SET_IS_DOWNLOAD_FAILED;
  payload: boolean;
};

type SetTabDownloadingAction = {
  type: AllOrdersActionsType.SET_TAB_DOWNLOADING;
  payload: {
    tab: "allOrders" | "unsubmittedOrders";
    isDownloading: boolean;
  };
};

type SetTabDownloadFailedAction = {
  type: AllOrdersActionsType.SET_TAB_DOWNLOAD_FAILED;
  payload: {
    tab: "allOrders" | "unsubmittedOrders";
    isDownloadFailed: boolean;
  };
};

type SetIsAuthorizationErrorAction = {
  type: AllOrdersActionsType.SET_IS_AUTHORIZATION_ERROR;
  payload: boolean;
};

type SetIsAuthenticationErrorAction = {
  type: AllOrdersActionsType.SET_IS_AUTHENTICATION_ERROR;
  payload: boolean;
};

type SetFilterAction = {
  type: AllOrdersActionsType.SET_FILTER;
  payload: AllOrdersFilters;
};

type SetLanguageAction = {
  type: AllOrdersActionsType.SET_LANGUAGE;
  payload: string;
};

type SetPageRowSizeAction = {
  type: AllOrdersActionsType.SET_PAGE_ROW_SIZE;
  payload: number;
};

type SetCurrentPageAction = {
  type: AllOrdersActionsType.SET_CURRENT_PAGE;
  payload: number;
};
type SetTotalOrdersAction = {
  type: AllOrdersActionsType.SET_TOTAL_ORDERS;
  paylaod: number;
};

type UpdateSelectedAccountsAction = {
  type: AllOrdersActionsType.UPDATE_SELECTED_ACCOUNTS;
  payload: DealerAccount[];
};

type SetSortModelAction = {
  type: AllOrdersActionsType.SET_SORT_MODEL;
  payload: GridSortModel;
};

type SetDateRangeFilterAction = {
  type: AllOrdersActionsType.SET_DATE_RANGE_FILTER_ACTION;
  payload: {
    startDate: Dayjs;
    endDate: Dayjs;
  };
};

type SetTabIndexAction = {
  type: AllOrdersActionsType.SET_TAB_INDEX;
  payload: number;
};

type SetStatusCountAction = {
  type: AllOrdersActionsType.SET_STATUS_COUNT;
  payload: OrderStatusCount;
};

export type AllOrdersActions =
  | SetAllOrdersAction
  | SetIsLoadingAction
  | SetIsDownloadingAction
  | SetIsDownloadFailedErrorAction
  | SetTabDownloadingAction
  | SetTabDownloadFailedAction
  | SetIsTechnicalErrorAction
  | SetIsAuthenticationErrorAction
  | SetIsAuthorizationErrorAction
  | SetFilterAction
  | SetLanguageAction
  | SetPageRowSizeAction
  | SetCurrentPageAction
  | SetTotalOrdersAction
  | UpdateSelectedAccountsAction
  | SetSortModelAction
  | SetDateRangeFilterAction
  | SetTabIndexAction
  | SetStatusCountAction;

export function setAllOrdersAction(payload: {
  allOrders: OrdersResponse[];
  totalOrders: number;
}): SetAllOrdersAction {
  return {
    type: AllOrdersActionsType.SET_ALL_ORDERS,
    payload,
  };
}
export function setIsLoadingAction(isLoading: boolean): SetIsLoadingAction {
  return {
    type: AllOrdersActionsType.SET_IS_LOADING,
    payload: isLoading,
  };
}

export function setIsDownloadingAction(
  isDownloading: boolean
): SetIsDownloadingAction {
  return {
    type: AllOrdersActionsType.SET_IS_DOWNLOADING,
    payload: isDownloading,
  };
}

export function setIsTechnicalErrorAction(
  isTechnicalError: boolean
): SetIsTechnicalErrorAction {
  return {
    type: AllOrdersActionsType.SET_IS_TECHNICAL_ERROR,
    payload: isTechnicalError,
  };
}

export function setIsDownloadFailedErrorAction(
  isDownloadFailed: boolean
): SetIsDownloadFailedErrorAction {
  return {
    type: AllOrdersActionsType.SET_IS_DOWNLOAD_FAILED,
    payload: isDownloadFailed,
  };
}

export function setTabDownloadingAction(
  tab: "allOrders" | "unsubmittedOrders",
  isDownloading: boolean
): SetTabDownloadingAction {
  return {
    type: AllOrdersActionsType.SET_TAB_DOWNLOADING,
    payload: { tab, isDownloading },
  };
}

export function setTabDownloadFailedAction(
  tab: "allOrders" | "unsubmittedOrders",
  isDownloadFailed: boolean
): SetTabDownloadFailedAction {
  return {
    type: AllOrdersActionsType.SET_TAB_DOWNLOAD_FAILED,
    payload: { tab, isDownloadFailed },
  };
}

export function setIsAuthorizationErrorAction(
  isAuthorizationError: boolean
): SetIsAuthorizationErrorAction {
  return {
    type: AllOrdersActionsType.SET_IS_AUTHORIZATION_ERROR,
    payload: isAuthorizationError,
  };
}

export function setIsAuthenticationErrorAction(
  isAuthenticationError: boolean
): SetIsAuthenticationErrorAction {
  return {
    type: AllOrdersActionsType.SET_IS_AUTHENTICATION_ERROR,
    payload: isAuthenticationError,
  };
}

export function setFilterAction(
  ordersByLineItemFilter: AllOrdersFilters
): SetFilterAction {
  return {
    type: AllOrdersActionsType.SET_FILTER,
    payload: ordersByLineItemFilter,
  };
}

export function setLanguageAction(language: string): SetLanguageAction {
  return {
    type: AllOrdersActionsType.SET_LANGUAGE,
    payload: language,
  };
}

export function setPageRowSizeAction(
  pageRowSize: number
): SetPageRowSizeAction {
  return {
    type: AllOrdersActionsType.SET_PAGE_ROW_SIZE,
    payload: pageRowSize,
  };
}

export function setCurrentPageAction(
  currentPage: number
): SetCurrentPageAction {
  return {
    type: AllOrdersActionsType.SET_CURRENT_PAGE,
    payload: currentPage,
  };
}

export function setTotalOrdersAction(
  totalOrders: number
): SetTotalOrdersAction {
  return {
    type: AllOrdersActionsType.SET_TOTAL_ORDERS,
    paylaod: totalOrders,
  };
}

export function updateSelectedAccountsAction(
  updateSelectedAccountsPayload: DealerAccount[]
): UpdateSelectedAccountsAction {
  return {
    type: AllOrdersActionsType.UPDATE_SELECTED_ACCOUNTS,
    payload: updateSelectedAccountsPayload,
  };
}

export function setSortModelAction(
  sortModel: GridSortModel
): SetSortModelAction {
  return {
    type: AllOrdersActionsType.SET_SORT_MODEL,
    payload: sortModel,
  };
}

export function setDateRangeFilterAction(payload: {
  startDate: Dayjs;
  endDate: Dayjs;
}): SetDateRangeFilterAction {
  return {
    type: AllOrdersActionsType.SET_DATE_RANGE_FILTER_ACTION,
    payload,
  };
}

export function setTabIndexAction(tabIndex: number): SetTabIndexAction {
  return {
    type: AllOrdersActionsType.SET_TAB_INDEX,
    payload: tabIndex,
  };
}

export function setStatusCountAction(
  statusCount: OrderStatusCount
): SetStatusCountAction {
  return {
    type: AllOrdersActionsType.SET_STATUS_COUNT,
    payload: statusCount,
  };
}
